import { TuiDay, TuiDayRange, TuiTime } from '@taiga-ui/cdk';
import { DateTime, DateTimeOptions, Interval } from 'luxon';

import { assertValid, Valid } from '../luxon/luxon.fns';

/**
 * Build an ISO-8601 datetime string in the corresponding timezone,
 * interpreting the given day/time in the supplied zone (defaults to UTC).
 *
 * @param day   a calendar date (month is 0-based in {@link TuiDay})
 * @param time  a time-of-day
 * @param zone  an IANA zone name, e.g. "Europe/Berlin" or "America/New_York"
 * @returns     an ISO string like "2025-04-23T13:30:00.000Z"
 */
export function toIsoDatetimeString(day: TuiDay, time: TuiTime | null, zone: string = 'UTC'): string {
  // Luxon expects month 1–12; TuiDay.month is 0–11
  const dt = DateTime.fromObject(
    {
      year: day.year,
      month: day.month + 1,
      day: day.day,
      hour: time?.hours ?? 0,
      minute: time?.minutes ?? 0,
      second: time?.seconds ?? 0,
      millisecond: time?.ms ?? 0,
    },
    { zone },
  );

  return dt.toISO() ?? '';
}

/**
 * Build an ISO-8601 date string from a {@link TuiDay}.
 * No need for a time zone as we are only interested in the date.
 *
 * @param day   a calendar date (month is 0-based in {@link TuiDay})
 * @returns     an ISO string like "2025-04-23T13:30:00.000Z"
 */
export function toIsoDateString(day: TuiDay | null): string {
  if (!day) {
    return '';
  }

  // Luxon expects month 1–12; TuiDay.month is 0–11
  const dt = DateTime.fromObject({
    year: day.year,
    month: day.month + 1,
    day: day.day,
  });

  return dt.toISODate() ?? '';
}

/**
 * Parse an ISO-8601 date string into a {@link TuiDay} (year, 0-based month, day)
 *
 * @param iso      an ISO string, e.g. "2025-04-23T13:30:00.000Z" or "2025-04-23T15:30"
 * @returns        a TuiDay with month zero-based
 */
export function fromIsoToTuiDay(iso: string): TuiDay {
  const dt = assertValid(DateTime.fromISO(iso, { setZone: true }));

  // TuiDay expects month 0–11
  return new TuiDay(dt.year, dt.month - 1, dt.day);
}

/**
 * Parse an ISO-8601 time (with optional date/offset) into a {@link TuiTime}.
 *
 * @param iso   an ISO string, e.g. "15:30", "15:30:45.123", "2025-04-23T13:30:00.000Z"
 * @returns     a TuiTime(hour, minute, second, millisecond)
 */
export function fromIsoToTuiTime(iso: string): TuiTime {
  const dt = assertValid(DateTime.fromISO(iso, { setZone: true }));

  return new TuiTime(dt.hour, dt.minute, dt.second, dt.millisecond);
}

/**
 * Parse an ISO-8601 datetime into a [{@link TuiDay}, {@link TuiTime}] tuple.
 *
 * @param iso   ISO string, e.g. "2025-04-23T15:30:00+02:00" or "2025-04-23T15:30"
 */
export function fromIsoToTuiDayAndTime(iso: string): [TuiDay, TuiTime] {
  const day = fromIsoToTuiDay(iso);
  const time = fromIsoToTuiTime(iso);
  return [day, time];
}

/**
 * Calculate an {@link Interval} from a {@link TuiDayRange} which start at the beginning of first day and ends on end of last day.
 *
 * @param range - The {@link TuiDayRange} to convert
 * @param opts - Luxon options for further configuration
 */
export function getFullIntervalFromTuiDayRange(range: TuiDayRange, opts?: DateTimeOptions): Interval<Valid> {
  const from = DateTime.fromISO(range.from.toJSON(), opts);
  const to = DateTime.fromISO(range.to.toJSON(), opts);
  return assertValid(Interval.fromDateTimes(from.startOf('day'), to.endOf('day')));
}
